import { useState, useRef } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Tag, Plus } from 'lucide-react';

const TAG_COLORS = ['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444', '#6B7280'];

export default function InlineTagPicker({ selectedNote, tags, updateNote, selectedNoteId, createTag }) {
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [newTagColor, setNewTagColor] = useState('#F59E0B');
  const tagInputRef = useRef(null);

  const noteTags = (selectedNote.tags || []).map((tid) => tags.find((t) => t.id === tid)).filter(Boolean);

  const filteredTags = tags.filter(tag =>
    tag.name.toLowerCase().includes(tagSearch.toLowerCase())
  );

  const exactMatch = tags.some(tag =>
    tag.name.toLowerCase() === tagSearch.toLowerCase()
  );

  const handleTagToggle = async (tagId) => {
    if (!selectedNote) return;
    const currentTags = selectedNote.tags || [];
    const newTags = currentTags.includes(tagId)
      ? currentTags.filter((t) => t !== tagId)
      : [...currentTags, tagId];
    await updateNote(selectedNoteId, { tags: newTags });
    setShowTagPicker(false);
  };

  const handleCreateTagInline = async (name) => {
    if (!name.trim()) return;
    const newTag = await createTag({ name: name.trim(), color: newTagColor });
    if (newTag && selectedNote) {
      const currentTags = selectedNote.tags || [];
      if (!currentTags.includes(newTag.id)) {
        await updateNote(selectedNote.id, { tags: [...currentTags, newTag.id] });
      }
    }
    setTagSearch('');
    setNewTagColor(TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)]);
  };

  return (
    <>
      {noteTags.map((tag) => (
        <span
          key={tag.id}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full border"
          style={{ backgroundColor: tag.color + '15', borderColor: tag.color + '40', color: tag.color }}
          data-testid={`editor-tag-badge-${tag.id}`}
        >
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
          {tag.name}
          <button
            onClick={() => handleTagToggle(tag.id)}
            className="ml-0.5 p-0.5 rounded-full hover:bg-black/20 transition-colors"
            title={`Remove ${tag.name}`}
            data-testid={`editor-tag-remove-${tag.id}`}
          >
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </span>
      ))}

      <Popover open={showTagPicker} onOpenChange={(open) => { setShowTagPicker(open); if (!open) setTagSearch(''); }}>
        <PopoverTrigger asChild>
          <button
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-dashed border-border/50 text-muted-foreground hover:border-border hover:bg-muted/30 transition-colors"
            data-testid="editor-inline-add-tag-btn"
          >
            <Tag className="w-3.5 h-3.5" /><span>Add Tag</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2" align="start" data-testid="editor-tag-picker">
          <div className="relative mb-2">
            <input
              ref={tagInputRef}
              type="text"
              value={tagSearch}
              onChange={(e) => setTagSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && tagSearch.trim() && !exactMatch) handleCreateTagInline(tagSearch); }}
              placeholder="Search or create tag..."
              className="w-full h-8 px-2.5 text-xs bg-secondary/50 border border-border rounded-md outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground/50"
              autoFocus
              data-testid="editor-tag-search-input"
            />
          </div>
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {filteredTags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => handleTagToggle(tag.id)}
                data-testid={`editor-tag-toggle-${tag.id}`}
                className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md transition-colors ${
                  (selectedNote.tags || []).includes(tag.id) ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/50'
                }`}
              >
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                <span className="flex-1 text-left truncate">{tag.name}</span>
                {(selectedNote.tags || []).includes(tag.id) && (
                  <svg className="w-3.5 h-3.5 text-accent-amber flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            ))}
            {tagSearch.trim() && !exactMatch && (
              <button
                onClick={() => handleCreateTagInline(tagSearch)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md text-muted-foreground hover:bg-muted/50 border-t border-border/50 mt-1 pt-2"
                data-testid="editor-tag-create-btn"
              >
                <span className="w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-border" style={{ backgroundColor: newTagColor }} />
                <span className="flex-1 text-left">Create "<span className="font-medium text-foreground">{tagSearch.trim()}</span>"</span>
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
            {tags.length === 0 && !tagSearch && (
              <p className="text-xs text-muted-foreground/60 text-center py-3">Type to create your first tag</p>
            )}
            {filteredTags.length === 0 && tagSearch && exactMatch && (
              <p className="text-xs text-muted-foreground/60 text-center py-2">No more tags found</p>
            )}
          </div>
          {tagSearch.trim() && !exactMatch && (
            <div className="mt-2 pt-2 border-t border-border/50">
              <p className="text-[10px] text-muted-foreground/60 mb-1.5">Tag color:</p>
              <div className="flex gap-1.5">
                {TAG_COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setNewTagColor(color)}
                    className={`w-5 h-5 rounded-full transition-transform ${
                      newTagColor === color ? 'scale-110 ring-2 ring-offset-1 ring-offset-background ring-white/50' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    data-testid={`editor-tag-color-${color}`}
                  />
                ))}
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}
